import {
  faChartBar,
  faChartLine,
  faChartPie,
  faChevronDown,
  faChevronUp,
  faFilter,
  faWandMagicSparkles,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Bar, Doughnut, HorizontalBar } from "react-chartjs-2";
import { useIntl } from "react-intl";
import { Button, Collapse, Col, Row } from "reactstrap";
import { useBodyDarkMode } from "../../../hooks/useBodyDarkMode";

/** Paleta “pro” alineada con el dashboard IA (violeta / cian / ámbar / índigo) */
const PALETTE = [
  "rgba(99, 102, 241, 0.85)",
  "rgba(6, 182, 212, 0.85)",
  "rgba(245, 158, 11, 0.88)",
  "rgba(168, 85, 247, 0.85)",
  "rgba(59, 130, 246, 0.85)",
  "rgba(236, 72, 153, 0.82)",
  "rgba(20, 184, 166, 0.85)",
  "rgba(251, 113, 133, 0.85)",
];

const PALETTE_BORDER = [
  "rgba(255, 255, 255, 0.95)",
  "rgba(255, 255, 255, 0.95)",
  "rgba(255, 255, 255, 0.95)",
  "rgba(255, 255, 255, 0.95)",
  "rgba(255, 255, 255, 0.95)",
  "rgba(255, 255, 255, 0.95)",
  "rgba(255, 255, 255, 0.95)",
  "rgba(255, 255, 255, 0.95)",
];

const STORAGE_VIS = "wembii.admin.charts.visibility";
const STORAGE_COLLAPSE = "wembii.admin.charts.collapsed";

type ChartVisibility = {
  plans: boolean;
  credits: boolean;
  signups: boolean;
};

const defaultVisibility = (): ChartVisibility => ({
  plans: true,
  credits: true,
  signups: true,
});

function loadVisibility(): ChartVisibility {
  try {
    const raw = sessionStorage.getItem(STORAGE_VIS);
    if (!raw) return defaultVisibility();
    const p = JSON.parse(raw) as Partial<ChartVisibility>;
    return { ...defaultVisibility(), ...p };
  } catch {
    return defaultVisibility();
  }
}

function loadCollapsed(): boolean {
  try {
    return sessionStorage.getItem(STORAGE_COLLAPSE) === "1";
  } catch {
    return false;
  }
}

export interface WembiiAdminDashboardChartsProps {
  planLabels: string[];
  planValues: number[];
  creditsLabels: string[];
  creditsValues: number[];
  signupLabels: string[];
  signupValues: number[];
}

export function WembiiAdminDashboardCharts(props: WembiiAdminDashboardChartsProps) {
  const {
    planLabels,
    planValues,
    creditsLabels,
    creditsValues,
    signupLabels,
    signupValues,
  } = props;
  const intl = useIntl();
  const isDark = useBodyDarkMode();

  const [chartsOpen, setChartsOpen] = useState(() => !loadCollapsed());
  const [vis, setVis] = useState<ChartVisibility>(() => loadVisibility());

  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_VIS, JSON.stringify(vis));
    } catch {
      /* ignore */
    }
  }, [vis]);

  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_COLLAPSE, chartsOpen ? "0" : "1");
    } catch {
      /* ignore */
    }
  }, [chartsOpen]);

  const toggleChart = useCallback((key: keyof ChartVisibility) => {
    setVis((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const resetVisibility = useCallback(() => {
    setVis(defaultVisibility());
  }, []);

  const onChartsCollapseEntered = useCallback(() => {
    window.requestAnimationFrame(() => {
      window.dispatchEvent(new Event("resize"));
    });
  }, []);

  const tickColor = isDark ? "#ffffff" : "#64748b";
  const gridColor = isDark ? "rgba(148, 163, 184, 0.12)" : "rgba(148, 163, 184, 0.25)";

  const doughnutData = useMemo(
    () => ({
      labels: planLabels,
      datasets: [
        {
          data: planValues,
          backgroundColor: planLabels.map((_, i) => PALETTE[i % PALETTE.length]),
          borderColor: PALETTE_BORDER.slice(0, planLabels.length),
          borderWidth: 2,
          hoverBorderWidth: 3,
          hoverOffset: 4,
        },
      ],
    }),
    [planLabels, planValues]
  );

  const doughnutOptions = useMemo(
    () => ({
      maintainAspectRatio: false,
      responsive: true,
      animation: { duration: 600 },
      cutoutPercentage: 62,
      legend: {
        display: true,
        position: "bottom" as const,
        labels: {
          boxWidth: 12,
          padding: 14,
          fontColor: tickColor,
          fontFamily:
            'var(--wembii-font-sans, "Inter", system-ui, sans-serif)',
          fontSize: 11,
        },
      },
      tooltips: {
        backgroundColor: isDark ? "rgba(15, 23, 42, 0.92)" : "rgba(255, 255, 255, 0.98)",
        titleFontColor: isDark ? "#ffffff" : "#0f172a",
        bodyFontColor: isDark ? "#ffffff" : "#334155",
        borderColor: "rgba(99, 102, 241, 0.35)",
        borderWidth: 1,
        xPadding: 12,
        yPadding: 10,
        callbacks: {
          label(
            tooltipItem: { index: number },
            data: { labels?: string[]; datasets: { data: number[] }[] }
          ) {
            const i = tooltipItem.index;
            const label = data.labels?.[i] ?? "";
            const v = data.datasets[0].data[i];
            return ` ${label}: ${v}`;
          },
        },
      },
      layout: { padding: { top: 8, bottom: 8, left: 8, right: 8 } },
    }),
    [isDark, tickColor]
  );

  const horizontalBarDataFixed = useMemo(
    () => ({
      labels: creditsLabels,
      datasets: [
        {
          label: intl.formatMessage({
            id: "app.adminDashboard.charts.creditsDataset",
          }),
          data: creditsValues,
          backgroundColor: creditsLabels.map((_, i) => PALETTE[i % PALETTE.length]),
          borderColor: creditsLabels.map(() => "rgba(255,255,255,0.35)"),
          borderWidth: 1,
        },
      ],
    }),
    [creditsLabels, creditsValues, intl]
  );

  const horizontalBarOptions = useMemo(
    () => ({
      maintainAspectRatio: false,
      responsive: true,
      animation: { duration: 600 },
      legend: { display: false },
      scales: {
        xAxes: [
          {
            ticks: {
              beginAtZero: true,
              fontColor: tickColor,
              fontFamily:
                'var(--wembii-font-sans, "Inter", system-ui, sans-serif)',
            },
            gridLines: {
              color: gridColor,
              zeroLineColor: gridColor,
            },
          },
        ],
        yAxes: [
          {
            ticks: {
              fontColor: tickColor,
              fontFamily:
                'var(--wembii-font-sans, "Inter", system-ui, sans-serif)',
            },
            gridLines: { display: false },
          },
        ],
      },
      tooltips: {
        backgroundColor: isDark ? "rgba(15, 23, 42, 0.92)" : "rgba(255, 255, 255, 0.98)",
        titleFontColor: isDark ? "#ffffff" : "#0f172a",
        bodyFontColor: isDark ? "#ffffff" : "#334155",
        borderColor: "rgba(99, 102, 241, 0.35)",
        borderWidth: 1,
      },
      layout: { padding: { left: 4, right: 12, top: 4, bottom: 4 } },
    }),
    [gridColor, isDark, tickColor]
  );

  const barGradient = useMemo(() => {
    const g: string[] = [];
    for (let i = 0; i < signupLabels.length; i += 1) {
      g.push(PALETTE[i % PALETTE.length]);
    }
    return g;
  }, [signupLabels.length]);

  const signupsBarData = useMemo(
    () => ({
      labels: signupLabels,
      datasets: [
        {
          label: intl.formatMessage({
            id: "app.adminDashboard.charts.signupsDataset",
          }),
          data: signupValues,
          backgroundColor: barGradient,
          borderColor: "rgba(255,255,255,0.4)",
          borderWidth: 1,
        },
      ],
    }),
    [barGradient, intl, signupLabels, signupValues]
  );

  const signupsBarOptions = useMemo(
    () => ({
      maintainAspectRatio: false,
      responsive: true,
      animation: { duration: 600 },
      legend: { display: false },
      scales: {
        yAxes: [
          {
            ticks: {
              beginAtZero: true,
              precision: 0,
              fontColor: tickColor,
              fontFamily:
                'var(--wembii-font-sans, "Inter", system-ui, sans-serif)',
            },
            gridLines: {
              color: gridColor,
              zeroLineColor: gridColor,
            },
          },
        ],
        xAxes: [
          {
            ticks: {
              fontColor: tickColor,
              fontFamily:
                'var(--wembii-font-sans, "Inter", system-ui, sans-serif)',
              maxRotation: 45,
              minRotation: 0,
            },
            gridLines: { display: false },
          },
        ],
      },
      tooltips: {
        backgroundColor: isDark ? "rgba(15, 23, 42, 0.92)" : "rgba(255, 255, 255, 0.98)",
        titleFontColor: isDark ? "#ffffff" : "#0f172a",
        bodyFontColor: isDark ? "#ffffff" : "#334155",
        borderColor: "rgba(99, 102, 241, 0.35)",
        borderWidth: 1,
      },
      layout: { padding: { left: 4, right: 4, top: 8, bottom: 4 } },
    }),
    [gridColor, isDark, tickColor]
  );

  const hasPlans = planLabels.length > 0 && planValues.some((v) => v > 0);
  const hasCredits = creditsLabels.length > 0 && creditsValues.some((v) => v > 0);
  const hasSignups = signupLabels.length > 0 && signupValues.some((v) => v > 0);

  const anyVisible = vis.plans || vis.credits || vis.signups;
  const showPlansCol = vis.plans;
  const showCreditsCol = vis.credits;
  const bothNarrow = showPlansCol && showCreditsCol;

  return (
    <div className="wembii-admin-charts-shell mb-4">
      <div className="wembii-admin-charts-shell__header">
        <div className="wembii-admin-charts-shell__title-row">
          <div className="wembii-admin-charts-shell__title-wrap">
            <span className="wembii-admin-charts-shell__badge">
              <FontAwesomeIcon icon={faWandMagicSparkles} className="mr-1" />
              {intl.formatMessage({ id: "app.adminDashboard.charts.badge" })}
            </span>
            <h5 className="wembii-admin-charts-shell__title mb-0">
              {intl.formatMessage({ id: "app.adminDashboard.charts.sectionTitle" })}
            </h5>
          </div>
          <Button
            color="link"
            className="wembii-admin-charts-shell__collapse-btn p-0"
            onClick={() => setChartsOpen((o) => !o)}
            aria-expanded={chartsOpen}
            type="button"
          >
            <FontAwesomeIcon icon={chartsOpen ? faChevronUp : faChevronDown} />
            <span className="ml-2 d-none d-sm-inline">
              {chartsOpen
                ? intl.formatMessage({ id: "app.adminDashboard.charts.collapse" })
                : intl.formatMessage({ id: "app.adminDashboard.charts.expand" })}
            </span>
          </Button>
        </div>
        <p className="wembii-admin-charts-shell__hint small mb-0">
          <FontAwesomeIcon icon={faFilter} className="mr-1 text-muted" />
          {intl.formatMessage({ id: "app.adminDashboard.charts.filterHint" })}
        </p>
      </div>

      <Collapse isOpen={chartsOpen} onEntered={onChartsCollapseEntered}>
        <div className="wembii-admin-charts-shell__toolbar">
          <span className="wembii-admin-charts-shell__toolbar-label">
            {intl.formatMessage({ id: "app.adminDashboard.charts.showLabel" })}
          </span>
          <button
            type="button"
            className={`wembii-admin-chart-toggle${vis.plans ? " is-active" : ""}`}
            onClick={() => toggleChart("plans")}
            aria-pressed={vis.plans}
          >
            <FontAwesomeIcon icon={faChartPie} className="mr-1" />
            {intl.formatMessage({ id: "app.adminDashboard.charts.togglePlans" })}
          </button>
          <button
            type="button"
            className={`wembii-admin-chart-toggle${vis.credits ? " is-active" : ""}`}
            onClick={() => toggleChart("credits")}
            aria-pressed={vis.credits}
          >
            <FontAwesomeIcon icon={faChartBar} className="mr-1" />
            {intl.formatMessage({ id: "app.adminDashboard.charts.toggleCredits" })}
          </button>
          <button
            type="button"
            className={`wembii-admin-chart-toggle${vis.signups ? " is-active" : ""}`}
            onClick={() => toggleChart("signups")}
            aria-pressed={vis.signups}
          >
            <FontAwesomeIcon icon={faChartLine} className="mr-1" />
            {intl.formatMessage({ id: "app.adminDashboard.charts.toggleSignups" })}
          </button>
          <button
            type="button"
            className="wembii-admin-chart-reset"
            onClick={resetVisibility}
          >
            {intl.formatMessage({ id: "app.adminDashboard.charts.resetView" })}
          </button>
        </div>

        {!anyVisible ? (
          <div className="wembii-admin-charts-shell__empty small text-muted">
            {intl.formatMessage({ id: "app.adminDashboard.charts.allHidden" })}
          </div>
        ) : (
          <div className="wembii-admin-charts-shell__body">
            {(showPlansCol || showCreditsCol) && (
              <Row className="g-3 mb-0">
                {showPlansCol && (
                  <Col xs="12" lg={bothNarrow ? "4" : "12"}>
                    <div className="wembii-admin-chart-card wembii-admin-chart-card--glow">
                      <div className="wembii-admin-chart-card__head">
                        <FontAwesomeIcon
                          icon={faChartPie}
                          className="wembii-admin-chart-card__icon"
                        />
                        <div className="wembii-admin-chart-card__title mb-0">
                          {intl.formatMessage({
                            id: "app.adminDashboard.charts.plansTitle",
                          })}
                        </div>
                      </div>
                      <div className="wembii-admin-chart-card__canvas">
                        {hasPlans ? (
                          <Doughnut data={doughnutData} options={doughnutOptions} />
                        ) : (
                          <p className="wembii-admin-chart-card__empty small text-muted mb-0">
                            {intl.formatMessage({
                              id: "app.adminDashboard.charts.emptyPlans",
                            })}
                          </p>
                        )}
                      </div>
                    </div>
                  </Col>
                )}
                {showCreditsCol && (
                  <Col xs="12" lg={bothNarrow ? "8" : "12"}>
                    <div className="wembii-admin-chart-card wembii-admin-chart-card--glow">
                      <div className="wembii-admin-chart-card__head">
                        <FontAwesomeIcon
                          icon={faChartBar}
                          className="wembii-admin-chart-card__icon"
                        />
                        <div className="wembii-admin-chart-card__title mb-0">
                          {intl.formatMessage({
                            id: "app.adminDashboard.charts.creditsTitle",
                          })}
                        </div>
                      </div>
                      <div className="wembii-admin-chart-card__canvas wembii-admin-chart-card__canvas--tall">
                        {hasCredits ? (
                          <HorizontalBar
                            data={horizontalBarDataFixed}
                            options={horizontalBarOptions}
                          />
                        ) : (
                          <p className="wembii-admin-chart-card__empty small text-muted mb-0">
                            {intl.formatMessage({
                              id: "app.adminDashboard.charts.emptyCredits",
                            })}
                          </p>
                        )}
                      </div>
                    </div>
                  </Col>
                )}
              </Row>
            )}

            {vis.signups && (
              <Row className="g-3 mt-1">
                <Col xs="12">
                  <div className="wembii-admin-chart-card wembii-admin-chart-card--glow">
                    <div className="wembii-admin-chart-card__head wembii-admin-chart-card__head--stack">
                      <FontAwesomeIcon
                        icon={faChartLine}
                        className="wembii-admin-chart-card__icon"
                      />
                      <div>
                        <div className="wembii-admin-chart-card__title mb-0">
                          {intl.formatMessage({
                            id: "app.adminDashboard.charts.signupsTitle",
                          })}
                        </div>
                        <p className="wembii-admin-chart-card__subtitle small text-muted mb-0">
                          {intl.formatMessage({
                            id: "app.adminDashboard.charts.signupsHint",
                          })}
                        </p>
                      </div>
                    </div>
                    <div className="wembii-admin-chart-card__canvas wembii-admin-chart-card__canvas--wide">
                      {hasSignups ? (
                        <Bar data={signupsBarData} options={signupsBarOptions} />
                      ) : (
                        <p className="wembii-admin-chart-card__empty small text-muted mb-0">
                          {intl.formatMessage({
                            id: "app.adminDashboard.charts.emptySignups",
                          })}
                        </p>
                      )}
                    </div>
                  </div>
                </Col>
              </Row>
            )}
          </div>
        )}
      </Collapse>
    </div>
  );
}
